import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Form, Card, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Select from 'react-select';
import { useOrderWizardContext } from '../OrderWizardProvider';
import budgetService from 'services/budgetService';
import { toast } from 'react-toastify';

const AdvancedConfigurationStep = ({ availableAccounts, loadingAccounts }) => {
  const { orderData, updateOrderData, validationErrors, isEditMode } = useOrderWizardContext();
  
  // Estados para campañas/cuentas reales
  const [realCampaigns, setRealCampaigns] = useState([]);
  const [realAccounts, setRealAccounts] = useState([]);
  const [loadingRealData, setLoadingRealData] = useState(false);
  const [realDataError, setRealDataError] = useState(null);


  const refreshRealAccounts = useCallback(async () => {
    if (!orderData.clienteId) return;
    
    setLoadingRealData(true);
    setRealDataError(null);
    
    try {
      const result = await budgetService.getAccountsByClient(orderData.clienteId);
      
      if (result.success) {
        const accounts = Array.isArray(result.accounts) ? result.accounts : [];
        setRealAccounts(accounts);
      } else {
        setRealDataError(`Error refrescando cuentas: ${result.message}`);
        setRealAccounts([]);
      }
    } catch (error) {
      setRealDataError('Error de conexión al refrescar cuentas');
      setRealAccounts([]);
    } finally {
      setLoadingRealData(false);
    }
  }, [orderData.clienteId]);


  useEffect(() => {
    const handleGlobalRefresh = (event) => {
      const { clientId } = event.detail || {};
      // Si el evento es para el cliente actual, refrescar
      if (!clientId || clientId === orderData.clienteId) {

        refreshRealAccounts();
      }
    };

    window.addEventListener('accounts:refresh', handleGlobalRefresh);
    
    return () => {
      window.removeEventListener('accounts:refresh', handleGlobalRefresh);
    };
  }, [refreshRealAccounts, orderData.clienteId]);

  // *** NUEVO: Mapeo de Business Units a tipos de cuentas permitidos ***
  const businessUnitAccountMapping = {
    'SOCIAL': ['Meta', 'TikTok', 'Facebook', 'Instagram', 'LinkedIn'],
    'SEARCH': ['Google'],
    'DISPLAY': ['Google'],
    'VIDEO': ['Google'],
    // TV, RADIO, PRINT no tienen filtro específico por ahora
  };

  // *** MODO EDICIÓN: Precargar cuentas y campañas automáticamente ***
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const preloadEditData = async () => {
      if (!isEditMode || !orderData.clienteId || !orderData.nivelAsignacion) {
        return;
      }

      setLoadingRealData(true);
      setRealDataError(null);

      try {
        // Siempre cargar cuentas primero
        const accountsResult = await budgetService.getAccountsByClient(orderData.clienteId);
        
        if (accountsResult.success) {
          const accounts = Array.isArray(accountsResult.accounts) ? accountsResult.accounts : [];
          setRealAccounts(accounts);
          

          // Si es nivel Campañas y hay selectedAccountForCampaigns, cargar las campañas
          if (orderData.nivelAsignacion === 'Campañas' && orderData.selectedAccountForCampaigns) {
            const campaignsResult = await budgetService.getCampaignsByClient(orderData.clienteId);
            
            if (campaignsResult.success) {
              const campaigns = Array.isArray(campaignsResult.campaigns) ? campaignsResult.campaigns : [];
              const selIdEdit = String(orderData.selectedAccountForCampaigns);
              const selectedAccount = accounts.find((acc) => String(acc.accountId) === selIdEdit);
              const dimKeysStr = selectedAccount?.dimensionalAccountKeys || selectedAccount?.dimensionalAccountKey || '';
              const dimKeysEdit = new Set(String(dimKeysStr).split(',').map(k => parseInt(k.trim(), 10)).filter(k => !isNaN(k)));

              let accountCampaigns = [];
              if (dimKeysEdit.size > 0) {
                accountCampaigns = campaigns.filter(campaign => {
                  const campaignAccountKey = parseInt(campaign.accountKey, 10);
                  return dimKeysEdit.has(campaignAccountKey);
                });
              } else if (selectedAccount?.accountName) {
                const nameNorm = String(selectedAccount.accountName).trim().toLowerCase();
                accountCampaigns = campaigns.filter(
                  (c) => String(c.accountName || '').trim().toLowerCase() === nameNorm,
                );
              }
              
              setRealCampaigns(accountCampaigns);
              setCampaignsCache(prev => ({ 
                ...prev, 
                [selIdEdit]: accountCampaigns 
              }));
            } else {
              setRealCampaigns([]);
            }
          } else {
            setRealCampaigns([]);
          }
        } else {
          setRealDataError(`Error cargando cuentas: ${accountsResult.message}`);
          setRealAccounts([]);
          setRealCampaigns([]);
        }
      } catch (error) {
        setRealDataError('Error de conexión al cargar datos');
        setRealAccounts([]);
        setRealCampaigns([]);
      } finally {
        setLoadingRealData(false);
      }
    };

    if (isEditMode) {
      preloadEditData();
    }
  }, [isEditMode, orderData.clienteId, orderData.nivelAsignacion, orderData.selectedAccountForCampaigns]);

  // *** CORREGIDO: Flujo mejorado para Campañas (solo para modo creación) ***
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Skip si estamos en modo edición (ya se maneja arriba)
    if (isEditMode) return;

    const loadRealData = async () => {
      // Solo cargar si hay cliente Y nivel seleccionado
      if (!orderData.clienteId || !orderData.nivelAsignacion) {
        setRealCampaigns([]);
        setRealAccounts([]);
        return;
      }

      setLoadingRealData(true);
      setRealDataError(null);

      try {
        if (orderData.nivelAsignacion === 'Cuenta') {
          // Para nivel "Cuenta": Cargar cuentas directamente
          const result = await budgetService.getAccountsByClient(orderData.clienteId);
          
          if (result.success) {
            const accounts = Array.isArray(result.accounts) ? result.accounts : [];
            setRealAccounts(accounts);
            setRealCampaigns([]); // Limpiar campañas
          } else {
            setRealDataError(`Error cargando cuentas: ${result.message}`);
            toast.error('Error cargando cuentas disponibles');
            setRealAccounts([]);
          }
        } else if (orderData.nivelAsignacion === 'Campañas') {
          // Para nivel "Campañas": Primero cargar cuentas para que el usuario seleccione
          const result = await budgetService.getAccountsByClient(orderData.clienteId);
          
          if (result.success) {
            const accounts = Array.isArray(result.accounts) ? result.accounts : [];
            setRealAccounts(accounts);
            setRealCampaigns([]); // Las campañas se cargarán después de seleccionar cuenta
          } else {
            setRealDataError(`Error cargando cuentas: ${result.message}`);
            toast.error('Error cargando cuentas disponibles');
            setRealAccounts([]);
          }
        }
      } catch (error) {
        setRealDataError('Error de conexión al cargar datos');
        toast.error('Error de conexión al cargar datos');
        setRealCampaigns([]);
        setRealAccounts([]);
      } finally {
        setLoadingRealData(false);
      }
    };

    // CAMBIO IMPORTANTE: Solo cargar cuando se selecciona el nivel, no cuando cambia el cliente
    if (orderData.nivelAsignacion && orderData.clienteId) {
      loadRealData();
    } else {
      // Limpiar datos si no hay nivel seleccionado
      setRealCampaigns([]);
      setRealAccounts([]);
    }
  }, [orderData.nivelAsignacion]); // Solo cuando cambie el nivel de asignación

  // *** NUEVO: Cargar campañas cuando se selecciona una cuenta (solo para nivel Campañas) ***
  const [campaignsCache, setCampaignsCache] = useState({}); // { accountKey: [campañas] }
  // const [lastLoadedAccount, setLastLoadedAccount] = useState(null); // No usado actualmente

  useEffect(() => {
    const loadCampaignsForAccount = async () => {
      if (orderData.nivelAsignacion !== 'Campañas' || !orderData.selectedAccountForCampaigns) {
        setRealCampaigns([]);
        return;
      }
      const selId = String(orderData.selectedAccountForCampaigns);
      const cached = campaignsCache[selId];
      // No reutilizar caché vacía: al cargar cuentas después, había que poder re-filtrar
      if (cached && Array.isArray(cached) && cached.length > 0) {
        setRealCampaigns(cached);
        return;
      }
      setLoadingRealData(true);
      setRealDataError(null);
      try {
        const result = await budgetService.getCampaignsByClient(orderData.clienteId);
        if (result.success) {
          const campaigns = Array.isArray(result.campaigns) ? result.campaigns : [];
          
          const selectedAccount = realAccounts.find(
            (acc) => String(acc.accountId) === selId,
          );
          const dimKeysStr = selectedAccount?.dimensionalAccountKeys || selectedAccount?.dimensionalAccountKey || '';
          const dimKeys = new Set(String(dimKeysStr).split(',').map(k => parseInt(k.trim(), 10)).filter(k => !isNaN(k)));
          
          let accountCampaigns = [];
          if (dimKeys.size > 0) {
            accountCampaigns = campaigns.filter(campaign => {
              const campaignAccountKey = parseInt(campaign.accountKey, 10);
              return dimKeys.has(campaignAccountKey);
            });
          } else if (selectedAccount?.accountName) {
            // Respaldo si aún no hay claves dimensionales: misma cuenta por nombre en hechos
            const nameNorm = String(selectedAccount.accountName).trim().toLowerCase();
            accountCampaigns = campaigns.filter(
              (c) => String(c.accountName || '').trim().toLowerCase() === nameNorm,
            );
          }
          
          let finalCampaigns = accountCampaigns;
          // Asegurar que la campaña ya asignada esté presente en la lista (modo edición)
          if (
            orderData.campaignId &&
            !accountCampaigns.some(c => String(c.campaignKey || c.campaignId) === String(orderData.campaignId))
          ) {
            finalCampaigns = [
              {
                campaignKey: orderData.campaignId,
                campaignId: orderData.campaignId,
                campaignName: `(Seleccionada) ${orderData.campaignId}`,
                accountKey: [...dimKeys][0] || null,
                accountName: '',
                platform: '',
                status: '',
                objective: ''
              },
              ...accountCampaigns
            ];
          }

          setRealCampaigns(finalCampaigns);
          setCampaignsCache((prev) => ({ ...prev, [selId]: finalCampaigns }));
          // setLastLoadedAccount(orderData.selectedAccountForCampaigns); // No usado actualmente
        } else {
          setRealDataError(`Error cargando campañas: ${result.message}`);
          toast.error('Error cargando campañas disponibles');
          setRealCampaigns([]);
        }
      } catch (error) {
        setRealDataError('Error de conexión al cargar campañas');
        toast.error('Error de conexión al cargar campañas');
        setRealCampaigns([]);
      } finally {
        setLoadingRealData(false);
      }
    };
    loadCampaignsForAccount();
  }, [
    orderData.selectedAccountForCampaigns,
    orderData.nivelAsignacion,
    orderData.clienteId,
    orderData.campaignId,
    realAccounts,
  ]);

  // Restaurar selección y campañas al volver del resumen
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const k = orderData.selectedAccountForCampaigns
      ? String(orderData.selectedAccountForCampaigns)
      : '';
    if (
      orderData.nivelAsignacion === 'Campañas' &&
      k &&
      campaignsCache[k]
    ) {
      setRealCampaigns(campaignsCache[k]);
    }
  }, [orderData.nivelAsignacion, orderData.selectedAccountForCampaigns]);

  // *** NUEVO: Función para filtrar cuentas según Business Unit ***
  const getFilteredAccounts = () => {
    if (!orderData.businessUnit || !availableAccounts) {
      return availableAccounts || [];
    }

    const allowedTypes = businessUnitAccountMapping[orderData.businessUnit];
    if (!allowedTypes) {
      return availableAccounts; // Si no hay mapeo, mostrar todas
    }

    // Filtrar tanto las cuentas reales como las disponibles según Business Unit
    const filtered = availableAccounts.filter(account => {
      const accountType = account.AccountType || '';
      const accountName = account.AccountName || '';
      
      // Verificar si el tipo de cuenta o el nombre contiene alguno de los tipos permitidos
      return allowedTypes.some(allowedType => 
        accountType.toLowerCase().includes(allowedType.toLowerCase()) ||
        accountName.toLowerCase().includes(allowedType.toLowerCase())
      );
    });

    return filtered;
  };

  // *** NUEVO: Función para filtrar cuentas reales según Business Unit ***
  const getFilteredRealAccounts = () => {
    if (!orderData.businessUnit || !realAccounts) {
      return realAccounts || [];
    }

    const allowedTypes = businessUnitAccountMapping[orderData.businessUnit];
    if (!allowedTypes) {
      return realAccounts; // Si no hay mapeo, mostrar todas
    }

    // Filtrar las cuentas reales según Business Unit
    const filtered = realAccounts.filter(account => {
      const accountName = account.accountName || '';
      const accountType = account.accountType || ''; // ✅ CORREGIDO: usar accountType
      
      // Verificar si el tipo de cuenta o el nombre contiene alguno de los tipos permitidos
      return allowedTypes.some(allowedType => 
        accountName.toLowerCase().includes(allowedType.toLowerCase()) ||
        accountType.toLowerCase().includes(allowedType.toLowerCase()) // ✅ CORREGIDO
      );
    });

    return filtered;
  };

  // *** NUEVO: Función para filtrar campañas reales según Business Unit ***
  const getFilteredRealCampaigns = () => {
    if (!orderData.businessUnit || !realCampaigns) {
      return realCampaigns || [];
    }

    const allowedTypes = businessUnitAccountMapping[orderData.businessUnit];
    if (!allowedTypes) {
      return realCampaigns; // Si no hay mapeo, mostrar todas
    }

    // Filtrar las campañas reales según Business Unit
    const filtered = realCampaigns.filter(campaign => {
      const accountName = campaign.accountName || '';
      const platform = campaign.platform || ''; // Las campañas sí tienen platform
      
      // Verificar si el nombre de cuenta o plataforma contiene alguno de los tipos permitidos
      return allowedTypes.some(allowedType => 
        accountName.toLowerCase().includes(allowedType.toLowerCase()) ||
        platform.toLowerCase().includes(allowedType.toLowerCase())
      );
    });

    return filtered;
  };

  const handleNivelAsignacionChange = (e) => {
    const nivel = e.target.value;
    updateOrderData({ 
      nivelAsignacion: nivel,
      cuentaId: '', // Limpiar selección anterior
      campaignId: '', // Limpiar campaña seleccionada
      accountId: '', // Limpiar cuenta seleccionada
      selectedAccountForCampaigns: '' // Limpiar cuenta seleccionada para campañas
    });
  };

  const handleRealCampaignChange = (selectedOptions) => {
    const selected = selectedOptions || [];
    const ids = selected.map(o => String(o.value));
    updateOrderData({
      campaignId: ids[0] || '',
      selectedCampaigns: selected.map(o => ({ campaignKey: String(o.value), campaignName: o.label })),
      campaignIds: ids,
    });
  };

  // *** NUEVO: Manejar selección de cuenta real ***
  const handleRealAccountChange = (e) => {
    const accountId = e.target.value;
    

    if (orderData.nivelAsignacion === 'Cuenta') {
      // Para nivel "Cuenta": Guardar directamente el accountId
      updateOrderData({ 
        accountId: accountId,
        campaignId: '', // Limpiar campaña anterior
        selectedAccountForCampaigns: '' // Limpiar cuenta para campañas
      });
    } else if (orderData.nivelAsignacion === 'Campañas') {
      // Para nivel "Campañas": Guardar la cuenta seleccionada para cargar sus campañas
      updateOrderData({ 
        selectedAccountForCampaigns: accountId,
        accountId: accountId, // Mantener sincronizado accountId (ID real)
        campaignId: '' // Limpiar campaña anterior
      });
    }
  };

  // *** NUEVO: Limpiar cuenta seleccionada cuando cambie Business Unit ***
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (orderData.businessUnit && orderData.cuentaId) {
      const filteredAccounts = getFilteredAccounts();
      const isCurrentAccountValid = filteredAccounts.some(
        account => account.AccountID === orderData.cuentaId
      );
      
      if (!isCurrentAccountValid) {
        updateOrderData({ cuentaId: '' });
      }
    }
  }, [orderData.businessUnit]); // Solo cuando cambie el Business Unit

  return (
    <div>
      <div className="mb-4">
        <h5 className="text-primary mb-2">
          <FontAwesomeIcon icon="cogs" className="me-2" />
          Configuración Avanzada
        </h5>
        <p className="text-muted mb-0">
          Configura los detalles técnicos de la orden de inserción
        </p>
      </div>

      <Row className="g-4">
        {/* Nivel de Asignación */}
        <Col xs={12}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom">
              <h6 className="text-warning mb-0">
                <FontAwesomeIcon icon="layer-group" className="me-2" />
                Nivel de Asignación
              </h6>
            </Card.Header>
            <Card.Body className="p-4">
              <Form.Group>
                <Form.Label className="fw-semibold">
                  Nivel de gestión de la orden <span className="text-danger">*</span>
                </Form.Label>
                <div className="mt-3">
                  <Form.Check
                    type="radio"
                    id="nivel-cuenta"
                    name="nivelAsignacion"
                    value="Cuenta"
                    label={
                      <div className="ms-2">
                        <div className="fw-semibold">Cuenta</div>
                        <small className="text-muted">
                          La orden se gestionará a nivel de cuenta publicitaria
                        </small>
                      </div>
                    }
                    checked={orderData.nivelAsignacion === 'Cuenta'}
                    onChange={handleNivelAsignacionChange}
                    className="mb-3"
                  />
                  <Form.Check
                    type="radio"
                    id="nivel-campanas"
                    name="nivelAsignacion"
                    value="Campañas"
                    label={
                      <div className="ms-2">
                        <div className="fw-semibold">Campañas</div>
                        <small className="text-muted">
                          La orden se dividirá en múltiples campañas específicas
                        </small>
                      </div>
                    }
                    checked={orderData.nivelAsignacion === 'Campañas'}
                    onChange={handleNivelAsignacionChange}
                  />
                </div>
                {!orderData.nivelAsignacion && (
                  <Form.Text className="text-muted">
                    <FontAwesomeIcon icon="lightbulb" className="me-1" />
                    Se recomienda "Cuenta" para órdenes simples y "Campañas" para estrategias complejas
                  </Form.Text>
                )}
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>

        {/* Etapa del Funnel */}
        <Col xs={12} md={6}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom">
              <h6 className="text-info mb-0">
                <FontAwesomeIcon icon="filter" className="me-2" />
                Etapa del Funnel
              </h6>
            </Card.Header>
            <Card.Body className="p-4">
              <Form.Group>
                <Form.Label className="fw-semibold">Etapa del funnel de marketing</Form.Label>
                <Form.Select
                  value={orderData.funnelStage || ''}
                  onChange={(e) => updateOrderData({ funnelStage: e.target.value })}
                  size="lg"
                >
                  <option value="">Sin asignar</option>
                  <option value="Upper">Upper (Notoriedad)</option>
                  <option value="Mid">Mid (Consideración)</option>
                  <option value="Lower">Lower (Acciones de valor)</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  <FontAwesomeIcon icon="lightbulb" className="me-1" />
                  Upper = Awareness/Branding, Mid = Consideración/Tráfico, Lower = Conversión/Performance
                </Form.Text>
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>

        {/* Selección de Cuentas (para ambos niveles) */}
        {orderData.clienteId && orderData.nivelAsignacion && (
          <Col xs={12}>
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white border-bottom">
                <h6 className="text-success mb-0">
                  <FontAwesomeIcon icon="bullseye" className="me-2" />
                  {orderData.nivelAsignacion === 'Campañas' ? 'Paso 1: Seleccionar Cuenta' : 'Seleccionar Cuenta'}
                </h6>
              </Card.Header>
              <Card.Body className="p-4">
                {loadingRealData && (
                  <div className="text-center py-3">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Cargando cuentas disponibles...
                  </div>
                )}

                {realDataError && (
                  <Alert variant="warning" className="mb-3">
                    <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
                    {realDataError}
                  </Alert>
                )}

                {!loadingRealData && !realDataError && (
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      Cuenta publicitaria <span className="text-danger">*</span>
                    </Form.Label>
                    
                    <Form.Select
                      value={orderData.nivelAsignacion === 'Cuenta' ? (orderData.accountId || '') : (orderData.selectedAccountForCampaigns || '')}
                      onChange={handleRealAccountChange}
                      isInvalid={!!validationErrors.accountId}
                      size="lg"
                    >
                      <option value="">Selecciona una cuenta...</option>
                      {Array.isArray(getFilteredRealAccounts()) && getFilteredRealAccounts().map(account => (
                        <option key={account.accountId} value={String(account.accountId)}>
                          {account.accountType ? `[${account.accountType}] ${account.accountName}` : account.accountName}
                        </option>
                      ))}
                    </Form.Select>
                    
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.accountId}
                    </Form.Control.Feedback>
                    
                    <Form.Text className="text-muted">
                      <FontAwesomeIcon icon="info-circle" className="me-1" />
                      {`${Array.isArray(getFilteredRealAccounts()) ? getFilteredRealAccounts().length : 0} cuenta(s) disponible(s) para ${orderData.businessUnit}`}
                      {orderData.nivelAsignacion === 'Campañas' && (
                        <div className="mt-1">
                          <FontAwesomeIcon icon="arrow-down" className="me-1" />
                          Después de seleccionar la cuenta, podrás elegir las campañas específicas
                        </div>
                      )}
                    </Form.Text>
                  </Form.Group>
                )}
              </Card.Body>
            </Card>
          </Col>
        )}

        {/* Selección de Campañas (solo para nivel Campañas y después de seleccionar cuenta) */}
        {orderData.nivelAsignacion === 'Campañas' && orderData.selectedAccountForCampaigns && (
          <Col xs={12}>
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white border-bottom">
                <h6 className="text-info mb-0">
                  <FontAwesomeIcon icon="flag" className="me-2" />
                  Paso 2: Seleccionar Campaña
                </h6>
              </Card.Header>
              <Card.Body className="p-4">
                {loadingRealData && (
                  <div className="text-center py-3">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Cargando campañas de la cuenta seleccionada...
                  </div>
                )}

                {realDataError && (
                  <Alert variant="warning" className="mb-3">
                    <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
                    {realDataError}
                  </Alert>
                )}

                {!loadingRealData && !realDataError && (
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      Campañas <span className="text-danger">*</span>
                    </Form.Label>

                    <Select
                      isMulti
                      isSearchable
                      placeholder="Buscar y seleccionar campañas..."
                      noOptionsMessage={() => 'Sin campañas disponibles'}
                      options={(Array.isArray(getFilteredRealCampaigns()) ? getFilteredRealCampaigns() : []).map(c => ({
                        value: String(c.campaignId || c.campaignKey),
                        label: c.campaignName,
                      }))}
                      value={(orderData.campaignIds || (orderData.campaignId ? [orderData.campaignId] : [])).map(id => {
                        const camp = (getFilteredRealCampaigns() || []).find(c => String(c.campaignId || c.campaignKey) === String(id));
                        return camp ? { value: String(id), label: camp.campaignName } : { value: String(id), label: `Campaña ${id}` };
                      })}
                      onChange={handleRealCampaignChange}
                      styles={{
                        control: (base) => ({ ...base, minHeight: '48px' }),
                        menu: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                      className={validationErrors.campaignId ? 'is-invalid' : ''}
                    />
                    {validationErrors.campaignId && (
                      <div className="invalid-feedback d-block">{validationErrors.campaignId}</div>
                    )}

                    <Form.Text className="text-muted">
                      <FontAwesomeIcon icon="info-circle" className="me-1" />
                      {`${Array.isArray(getFilteredRealCampaigns()) ? getFilteredRealCampaigns().length : 0} campaña(s) disponible(s) — puedes seleccionar una o varias`}
                    </Form.Text>
                  </Form.Group>
                )}
              </Card.Body>
            </Card>
          </Col>
        )}

        {/* Información de Progreso */}
        <Col xs={12}>
          <div className="mt-4 pt-3 border-top">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <small className="text-muted">Paso 3 de 4</small>
              </div>
              <div className="text-end">
                <small className="text-primary">
                  <FontAwesomeIcon icon="arrow-right" className="me-1" />
                  Siguiente: Resumen Final
                </small>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

AdvancedConfigurationStep.propTypes = {
  availableAccounts: PropTypes.array.isRequired,
  loadingAccounts: PropTypes.bool.isRequired
};

export default AdvancedConfigurationStep; 